import React from 'react';
import { useDispatch } from 'react-redux';
import { useAccount, useNetwork } from 'wagmi';
import { normalizeEther, normalizeNumber } from '../../helpers/digits';
import useGetCollection from '../../hooks/useGetCollection';
import useGetCollectionNftsByWallet from '../../hooks/useGetCollectionNftsByWallet';
import { getCdnUrl } from '../../lib/cdnUrl';
import { setModal } from '../../redux/counterSlice';
import useGetTokenPrice from '../../hooks/useGetTokenPrice';
import moment from 'moment';
import { formatEther } from 'viem';
import { getCoinGeckoWrappedTokenId, getTokenName } from '../../helpers/getChainName';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { useLendingContract, useTokenContract } from '../../helpers/setMarketPlaceAbi';
import { approveERC20 } from '../../helpers/blockchainOperations';
import { getSupportedChain } from '../../helpers/getSupportedChain';
import { useOrderStatusEffect } from './GlobalOfferItem';
import { ImageFixedAO } from '../Image_fixed_aspect_ratio';
import { ShowUser } from '../userImage';
import { switchNetwork } from 'wagmi/actions';

const getEnd = (endDate) => {
  const now = Math.floor(Date.now() / 1000);

  let range = parseInt(endDate) - now;

  if (range < 0) {
    return 'expired';
  } else if (range > 2628000) {
    const months = normalizeNumber(range / 2628000, 0);

    if (months === 1) return `in one month`;

    return `in ${months} months`;
  } else if (range > 86400) {
    return `in ${normalizeNumber(range / 86400, 0)} days`;
  } else {
    range = range % (24 * 3600);
    const hours = range / 3600;

    range %= 3600;
    const minutes = range / 60;

    return `in ${hours.toFixed(0)}:${minutes.toFixed(0)} hours`;
  }
};

const GlobalLendOfferItem = ({ offer }) => {
  const {
    id,
    issuer,
    amount,
    signingTime,
    duration,
    expiration,
    paymentTokenName,
    nftAddress: collectionAddress,
    borrowAmount,
    item,
    filledAmount,
    status,
    isExpired,
    interestRate,
  } = {
    ...offer,
    amount: Number(offer?.amount),
    expiration: Number(offer?.expiration),
    duration: Number(offer?.duration),
    signingTime: Number(offer?.signingTime),
  };
  const { usdPrice: tokenPrice } = useGetTokenPrice(
    item?.chain,
    offer?.paymentTokenName?.toLowerCase(),
  );
  const isOfferExpired =
    Number(signingTime) + Number(expiration) < Math.floor(Date.now() / 1000) || isExpired;
  const dispatch = useDispatch();
  // const bnbPrice = useGetUSDPriceOfToken('wbnb', 1).data?.wbnb.usd;
  const collection = useGetCollection(item?.collectionAddress).data;

  const { address, isConnected } = useAccount();
  const { chain: currentChain } = useNetwork();

  const price = normalizeEther(borrowAmount);

  const { data: userNfts, isLoading } = useGetCollectionNftsByWallet(collectionAddress, address);

  const assetAmount = Number(formatEther(borrowAmount));
  const paybackAmount = Number(
    assetAmount + (assetAmount / 100) * Number(interestRate / 100),
  ).toFixed(4);
  const amountWithFee = Number(paybackAmount);

  const lendingContract = useLendingContract();

  const signatureStillActive = useOrderStatusEffect(lendingContract, offer);
  const chain = getSupportedChain(offer?.item?.chain);
  const tokenContract = useTokenContract(chain?.id);

  return (
    <div
      className='overflow-hidden rounded-2lg border border-jacarta-100 bg-white dark:border-jacarta-700 dark:bg-jacarta-700'
      key={id}
    >
      <div className='overflow-hidden rounded-tl-2lg rounded-tr-2lg'>
        <ImageFixedAO
          image={getCdnUrl(
            offer?.fromUser?.profilePhoto
              ? offer?.fromUser?.profilePhoto
              : collection?.profilePhotoPath,
          )}
          alt={`${offer?.id}}`}
        />
      </div>
      <div className='flex flex-col items-center p-3 py-4'>
        <div className='text-md flex items-center space-x-1 font-bold'>
          {/* <ChainIcon name={item?.chain} tooltip={getTokenName(item?.chain)} sideGap={true} /> */}
          <span className='text-base font-medium tracking-tight text-green'>
            {normalizeEther(price)} {paymentTokenName}
          </span>
        </div>
        <span className='mt-2 text-center'>
          Payback:
          <span className='text-base font-medium tracking-tight text-green'>
            {' '}
            {normalizeEther(amountWithFee)} {paymentTokenName}
          </span>
        </span>

        <div className='mt-4'>
          <div className='text-center'>
            <span className='font-display'>Interest Rate: </span>
            {interestRate / 100}%
          </div>
          <div className='text-center'>
            <span className='font-display'>Offer: </span>
            {filledAmount || 0}/{amount} filled
          </div>
          <div className='text-center capitalize'>
            {/* <span className='font-display'>Terms: </span> */}
            {/* {price} {paymentTokenName} / {moment().add(duration, 'seconds').fromNow(true)} */}
            <span className='font-display'>Duration: </span>
            {moment().add(duration, 'seconds').fromNow(true)}
          </div>
          <div className='text-center'>
            <span className='font-display'>
              {status === 3 ? 'Canceled' : isOfferExpired ? 'Expired' : 'Expire: '}
            </span>
            {status === 0 && !isOfferExpired ? getEnd(signingTime + expiration) : ''}
          </div>
        </div>

        <div className='mt-4'>USD: ~ ${normalizeNumber(tokenPrice * price, 3)}</div>
        <div className='mt-2 flex items-center space-x-1'>
          <span>From: </span>
          <ShowUser user={offer?.fromUser} address={issuer} textSm={false} hideImage showName />
        </div>

        <div className='mt-4 flex w-full flex-col flex-wrap items-center justify-center space-y-3'>
          {(isConnected && !offer?.isReady) ||
            (isLoading && (
              <div className='alert-info rounded-lg p-3 text-center text-sm'>
                <span className='font-semibold'>Loading...</span>
              </div>
            ))}
          {typeof offer?.hasEnoughBalance === 'boolean' &&
          !offer?.hasEnoughBalance &&
          // !wrongNetwork &&
          isConnected &&
          !['canceled', 'filled'].includes(offer?.status) ? (
            <div className='alert-info rounded-lg p-3 text-center text-sm'>
              <span className='font-semibold'>
                The bidder has insufficient {getCoinGeckoWrappedTokenId(chain?.hexId).toUpperCase()}{' '}
                balance.{' '}
              </span>
              Conversion from {getTokenName(chain?.hexId)} to{' '}
              {getCoinGeckoWrappedTokenId(chain?.hexId).toUpperCase()} is done during the offer and
              if the {getCoinGeckoWrappedTokenId(chain?.hexId).toUpperCase()} balance falls below
              the offered value, the offer cannot be accepted.
            </div>
          ) : null}
          {isConnected &&
            !offer?.isApprovedEnough &&
            offer?.hasEnoughBalance &&
            signatureStillActive && (
              <div className='w-full items-center justify-center'>
                <div className='alert-error rounded-lg p-3 text-center text-sm'>
                  <span className='font-semibold'>
                    {/* The bidder has not approved enough{' '} */}
                    {isConnected && (
                      <div>
                        <span>
                          {issuer === address
                            ? 'You have not approved enough '
                            : 'The bidder has not approved enough '}
                          {getCoinGeckoWrappedTokenId(chain?.hexId).toUpperCase()} for this offer.
                          {` `}
                          {issuer === address &&
                            'You must set your approval as at least your bid amount.'}
                        </span>
                        {issuer === address && (
                          <>
                            <br />
                            <a
                              href='https://support.metamask.io/hc/en-us/articles/6174898326683-What-is-a-token-approval-'
                              className='mt-2 flex items-center justify-center text-accent'
                              rel='noreferrer'
                              target='_blank'
                            >
                              <span style={{ fontSize: '15px' }}>Learn More</span>
                              <FaExternalLinkAlt className='ml-1 inline-block' />
                            </a>
                          </>
                        )}
                      </div>
                    )}
                  </span>
                </div>
              </div>
            )}
          {isConnected &&
            !offer?.isApprovedEnough &&
            offer?.hasEnoughBalance &&
            signatureStillActive &&
            issuer === address && (
              <button
                className='rounded-xl bg-accent py-3 px-8 text-center font-semibold text-white transition-all hover:bg-accent-dark'
                onClick={async () => {
                  await approveERC20(
                    tokenContract,
                    address,
                    offer?.priceBigInt,
                    chain?.lendingContract,
                  );
                }}
                style={{
                  fontSize: '14px',
                }}
              >
                Approve {getCoinGeckoWrappedTokenId(chain?.hexId).toUpperCase()}
              </button>
            )}
          {isConnected &&
            issuer !== address &&
            status === 0 &&
            offer?.hasEnoughBalance &&
            signatureStillActive &&
            offer?.isApprovedEnough &&
            !isOfferExpired &&
            !['canceled', 'filled'].includes(offer?.status) &&
            userNfts?.length > 0 && (
              <button
                className='rounded-xl bg-accent py-3 px-8 text-center font-semibold text-white transition-all hover:bg-accent-dark'
                onClick={async () => {
                  if (chain?.hexId !== currentChain?.hexId)
                    await switchNetwork({
                      chainId: parseInt(chain?.hexId),
                    });
                  return dispatch(
                    setModal({
                      key: 'lendingModal',
                      data: {
                        ...offer,
                        type: 'accept',
                      },
                    }),
                  );
                }}
                style={{
                  fontSize: '14px',
                }}
              >
                Accept Offer
              </button>
            )}
          {isConnected && issuer === address && status === 0 && !isOfferExpired && (
            <button
              className='rounded-xl bg-accent py-3 px-8 text-center font-semibold text-white transition-all hover:bg-accent-dark'
              style={{
                fontSize: '14px',
              }}
              onClick={async () => {
                if (chain?.hexId !== currentChain?.hexId)
                  await switchNetwork({
                    chainId: parseInt(chain?.hexId),
                  });
                return dispatch(
                  setModal({
                    key: 'lendingModal',
                    data: {
                      ...offer,
                      type: 'cancel',
                    },
                  }),
                );
              }}
            >
              Cancel Offer
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
export default GlobalLendOfferItem;
